/**
 * Cross-calendar utilities — origin vacation windows overlaid on destination conditions.
 *
 * Unified origin holiday API:
 * - For existing 19 countries in cal_events: derives from getPublicHolidaysForCountry()
 * - For external origin countries (US, GB, AU): uses cal_origin_holidays table
 */

import { getPublicHolidaysForCountry, getOriginHolidays, getCountries } from './data-source';
import { addDaysToString } from './date-calc';
import { getLongWeekendsForCountry, type LongWeekend } from './long-weekends';
import type { CalendarEvent, CountryCode } from './types';
import type { OriginHoliday } from './db-supabase';

// Origin countries with data in cal_origin_holidays (not in cal_events)
const EXTERNAL_ORIGIN_CODES = ['US', 'GB', 'AU'] as const;
type ExternalOriginCode = typeof EXTERNAL_ORIGIN_CODES[number];

export interface OriginCountryDef {
  code: string;
  name: string;
  flag: string;
  isExternal: boolean; // true = from cal_origin_holidays, false = from cal_events
}

/** Returns all origin countries: existing 19 + external origin countries. */
export function getAllOriginCountries(): OriginCountryDef[] {
  const existing: OriginCountryDef[] = getCountries().map(c => ({
    code: c.code,
    name: c.name,
    flag: c.flag,
    isExternal: false,
  }));

  const existingCodes = new Set(existing.map(c => c.code));

  const external: OriginCountryDef[] = [
    { code: 'US', name: 'United States', flag: '\u{1F1FA}\u{1F1F8}', isExternal: true },
    { code: 'GB', name: 'United Kingdom', flag: '\u{1F1EC}\u{1F1E7}', isExternal: true },
    { code: 'AU', name: 'Australia', flag: '\u{1F1E6}\u{1F1FA}', isExternal: true },
  ].filter(e => !existingCodes.has(e.code));

  return [...existing, ...external];
}

function isExternalOrigin(code: string): code is ExternalOriginCode {
  return (EXTERNAL_ORIGIN_CODES as readonly string[]).includes(code);
}

export interface OriginVacationWindow {
  totalDaysOff: number;
  holidays: CalendarEvent[];
  longWeekends: LongWeekend[];
  /** Origin holidays from cal_origin_holidays (for external origins only) */
  originHolidays: OriginHoliday[];
}

/**
 * Returns vacation windows for an origin country in a specific month.
 * For existing 19 countries: uses cal_events public holidays + long weekends.
 * For external origins (US, GB, AU): uses cal_origin_holidays.
 */
export function getOriginVacationWindows(originCode: CountryCode | string, month: number): OriginVacationWindow {
  if (isExternalOrigin(originCode)) {
    return getExternalOriginWindows(originCode, month);
  }

  // Existing country — use cal_events data
  const allHolidays = getPublicHolidaysForCountry(originCode as CountryCode);
  const holidays = allHolidays.filter(e => {
    const m = parseInt(e.dateStart.slice(5, 7), 10);
    return m === month;
  });

  const allLw = getLongWeekendsForCountry(originCode as CountryCode);
  const longWeekends = allLw.filter(lw => {
    const startMonth = parseInt(lw.startDate.slice(5, 7), 10);
    const endMonth = parseInt(lw.endDate.slice(5, 7), 10);
    return startMonth === month || endMonth === month;
  });

  const offDates = new Set<string>();
  for (const lw of longWeekends) {
    const start = new Date(lw.startDate + 'T00:00:00');
    const end = new Date(lw.endDate + 'T00:00:00');
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (d.getMonth() + 1 === month) {
        offDates.add(d.toISOString().slice(0, 10));
      }
    }
  }
  for (const h of holidays) {
    offDates.add(h.dateStart);
  }

  return {
    totalDaysOff: offDates.size,
    holidays,
    longWeekends,
    originHolidays: [],
  };
}

/** Returns vacation windows for external origin countries using cal_origin_holidays. */
function getExternalOriginWindows(code: ExternalOriginCode, month: number): OriginVacationWindow {
  const allHolidays = getOriginHolidays(code);
  const monthHolidays = allHolidays.filter(h => {
    const dateStr = typeof h.date === 'string' ? h.date : '';
    const m = parseInt(dateStr.slice(5, 7), 10);
    return m === month;
  });

  const publicHolidays = monthHolidays.filter(h => h.type === 'public_holiday');
  const longWeekendCount = publicHolidays.filter(h => h.createsLongWeekend).length;

  // Count unique off dates
  const offDates = new Set<string>();
  for (const h of monthHolidays) {
    offDates.add(typeof h.date === 'string' ? h.date : '');
  }
  // Add weekends around long-weekend holidays
  for (const h of publicHolidays.filter(hol => hol.createsLongWeekend)) {
    const dateStr = typeof h.date === 'string' ? h.date : '';
    const d = new Date(dateStr + 'T00:00:00');
    const dow = d.getDay();
    // Add surrounding weekend days
    if (dow === 1) { // Monday — Sat+Sun before
      offDates.add(addDaysToString(d, -2));
      offDates.add(addDaysToString(d, -1));
    } else if (dow === 5) { // Friday — Sat+Sun after
      offDates.add(addDaysToString(d, 1));
      offDates.add(addDaysToString(d, 2));
    } else if (dow === 4) { // Thursday — take Fri off + Sat+Sun
      offDates.add(addDaysToString(d, 1));
      offDates.add(addDaysToString(d, 2));
      offDates.add(addDaysToString(d, 3));
    }
  }

  return {
    totalDaysOff: offDates.size,
    holidays: [], // No CalendarEvent objects for external origins
    longWeekends: [], // No LongWeekend objects for external origins
    originHolidays: monthHolidays,
  };
}

